import { API_ENDPOINTS } from '@/constants';
import { useMutation, useQuery } from '@tanstack/react-query';
import { get, handleResponse, post, put } from './base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import { FileUploadResponse } from '@/constants/Interfaces/CommonInterfaces';
import type {
  ApiResponse,
  CreateConfigurationPayload,
  DashboardData,
  UpdateConfigurationPayload
} from './types';

export const useUploadAssetsMutation = () =>
  useMutation<FileUploadResponse, Error, string>({
    mutationFn: (payload: string) => {
      return get(
        `${API_ENDPOINTS.UPLOAD_ASSETS}?${payload}`
      ) as Promise<FileUploadResponse>;
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetConfigurationListQuery = (payload: string) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(
          `${API_ENDPOINTS.CONFIGURATION_LIST}?${payload}`
        );
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return a default value to prevent undefined
        return { success: false, data: { items: [], total: 0 } };
      }
    },
    enabled: !!payload,
    refetchOnWindowFocus: true,
    queryKey: ['configuration-list', payload]
  });

export const useGetConfigurationDetailsQuery = (id: number | null) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(`${API_ENDPOINTS.CONFIGURATION_LIST}/${id}`);
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return a default value to prevent undefined
        return { success: false, data: null };
      }
    },
    enabled: !!id,
    refetchOnWindowFocus: true,
    queryKey: ['configuration-details', id]
  });

export const useAddConfigurationMutation = () =>
  useMutation<ApiResponse, Error, CreateConfigurationPayload>({
    mutationFn: (payload: CreateConfigurationPayload) => {
      return post(
        API_ENDPOINTS.CONFIGURATION_LIST,
        payload as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useEditConfigurationMutation = () =>
  useMutation<ApiResponse, Error, UpdateConfigurationPayload>({
    mutationFn: ({ id, payload }: UpdateConfigurationPayload) => {
      return put(`${API_ENDPOINTS.CONFIGURATION_LIST}/${id}`, payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetDashboardDataQuery = () =>
  useQuery<DashboardData | null>({
    queryFn: async () => {
      try {
        const response = await get<DashboardData>(API_ENDPOINTS.DASHBOARD_DATA);
        return handleResponse<DashboardData>(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return null to match handleResponse's return type on error
        return null;
      }
    },
    enabled: true,
    refetchOnWindowFocus: true,
    queryKey: ['dashboard-data'],
    staleTime: 0
  });

export const useGetUsersInformationQuery = () =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(API_ENDPOINTS.USERS_INFORMATION);
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return a default value to prevent undefined
        return { success: false, data: null };
      }
    },
    enabled: true,
    refetchOnWindowFocus: true,
    queryKey: ['users-information']
  });

export const useGetPrefectureListQuery = (payload: string) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(`${API_ENDPOINTS.PREFECTURE}?${payload}`);
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return a default value to prevent undefined
        return { success: false, data: { items: [] } };
      }
    },
    enabled: !!payload,
    refetchOnWindowFocus: true,
    queryKey: ['prefecture-list', payload]
  });
